import API_ROUTES from "../constants/api";
import HttpUtil from "../http-handler/http-util";

export default class GHGEmissionMasterDataService {
  // Datasets
  static listDatasets() {
    return HttpUtil.get(API_ROUTES.GHG_EMISSION_MASTER_DATA.LIST_DATASETS);
  }

  static getDataset(datasetName) {
    return HttpUtil.get(API_ROUTES.GHG_EMISSION_MASTER_DATA.GET_DATASET.replace("<datasetName>", datasetName));
  }

  // Versions
  static listVersions(datasetName, companyId = null, fetchAll = false) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.LIST_VERSIONS.replace("<datasetName>", datasetName);
    const queryParams = [];
    
    if (companyId) {
      queryParams.push(`companyId=${companyId}`);
    }
    if (fetchAll) {
      queryParams.push(`fetchAll=${fetchAll}`);
    }
    
    if (queryParams.length > 0) {
      url = url + `?${queryParams.join('&')}`;
    }
    
    return HttpUtil.get(url);
  }

  static createVersion(datasetName, payload, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.CREATE_VERSION.replace("<datasetName>", datasetName);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.post(url, payload);
  }

  static updateVersion(datasetName, versionNumber, payload, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.UPDATE_VERSION
      .replace("<datasetName>", datasetName)
      .replace("<versionNumber>", versionNumber);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.put(url, payload);
  }

  static cloneVersion(datasetName, sourceVersion, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.CLONE_VERSION
      .replace("<datasetName>", datasetName)
      .replace("<sourceVersion>", sourceVersion);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.post(url, {});
  }

  static deleteVersion(datasetName, versionNumber, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.DELETE_VERSION
      .replace("<datasetName>", datasetName)
      .replace("<versionNumber>", versionNumber);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.delete(url);
  }

  static exportVersion(datasetName, versionNumber, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.EXPORT_VERSION
      .replace("<datasetName>", datasetName)
      .replace("<versionNumber>", versionNumber);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.getExcelData(url);
  }

  static importVersion(datasetName, versionNumber, formData, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.IMPORT_VERSION
      .replace("<datasetName>", datasetName)
      .replace("<versionNumber>", versionNumber);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  // Emission Sources
  static listSources(datasetName, version, params = {}, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.LIST_SOURCES
      .replace("<datasetName>", datasetName)
      .replace("<version>", version);
    const queryParams = new URLSearchParams();
    
    if (params.pageIndex !== undefined) queryParams.append("pageNumber", params.pageIndex);
    if (params.pageSize) queryParams.append("pageSize", params.pageSize);
    if (params.sort) queryParams.append("sort", params.sort);
    if (params.category) queryParams.append("category", params.category);
    if (params.sourceType) queryParams.append("sourceType", params.sourceType);
    if (params.geography) queryParams.append("geography", params.geography);
    if (params.primaryScope) queryParams.append("primaryScope", params.primaryScope);
    if (params.name) queryParams.append("name", params.name);
    if (companyId) queryParams.append("companyId", companyId);

    if (queryParams.toString()) {
      url += "?" + queryParams.toString();
    }
    
    return HttpUtil.get(url);
  }

  static getSource(datasetName, version, sourceId, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.GET_SOURCE
      .replace("<datasetName>", datasetName)
      .replace("<version>", version)
      .replace("<sourceId>", sourceId);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.get(url);
  }

  static createSource(datasetName, version, payload, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.CREATE_SOURCE
      .replace("<datasetName>", datasetName)
      .replace("<version>", version);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.post(url, payload);
  }

  static updateSource(datasetName, version, sourceId, payload, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.UPDATE_SOURCE
      .replace("<datasetName>", datasetName)
      .replace("<version>", version)
      .replace("<sourceId>", sourceId);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.put(url, payload);
  }

  static deleteSource(datasetName, version, sourceId, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.DELETE_SOURCE
      .replace("<datasetName>", datasetName)
      .replace("<version>", version)
      .replace("<sourceId>", sourceId);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.delete(url);
  }

  // Emission Factors
  static listFactors(datasetName, version, params = {}, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.LIST_FACTORS
      .replace("<datasetName>", datasetName)
      .replace("<version>", version);
    const queryParams = new URLSearchParams();
    
    if (params.pageIndex !== undefined) queryParams.append("pageNumber", params.pageIndex);
    if (params.pageSize) queryParams.append("pageSize", params.pageSize);
    if (params.sort) queryParams.append("sort", params.sort);
    if (params.emissionSourceId) queryParams.append("emissionSourceId", params.emissionSourceId);
    if (params.ghg) queryParams.append("ghg", params.ghg);
    if (params.scope) queryParams.append("scope", params.scope);
    if (companyId) queryParams.append("companyId", companyId);

    if (queryParams.toString()) {
      url += "?" + queryParams.toString();
    }
    
    return HttpUtil.get(url);
  }

  static getFactor(datasetName, version, factorId, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.GET_FACTOR
      .replace("<datasetName>", datasetName)
      .replace("<version>", version)
      .replace("<factorId>", factorId);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.get(url);
  }

  static getFactorsBySource(datasetName, version, sourceId, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.GET_FACTORS_BY_SOURCE
      .replace("<datasetName>", datasetName)
      .replace("<version>", version)
      .replace("<sourceId>", sourceId);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.get(url);
  }

  static createFactor(datasetName, version, payload, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.CREATE_FACTOR
      .replace("<datasetName>", datasetName)
      .replace("<version>", version);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.post(url, payload);
  }

  static updateFactor(datasetName, version, factorId, payload, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.UPDATE_FACTOR
      .replace("<datasetName>", datasetName)
      .replace("<version>", version)
      .replace("<factorId>", factorId);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.put(url, payload);
  }

  static deleteFactor(datasetName, version, factorId, companyId = null) {
    let url = API_ROUTES.GHG_EMISSION_MASTER_DATA.DELETE_FACTOR
      .replace("<datasetName>", datasetName)
      .replace("<version>", version)
      .replace("<factorId>", factorId);
    if (companyId) {
      url = url + `?companyId=${companyId}`;
    }
    return HttpUtil.delete(url);
  }
}
